import { type AibackReportSummaryEntity } from '@/entities/aiback-report.entity';
import {
    ReportSummaryStreamEvent,
    type GetReportSummaryOptions,
} from '@/repositories/marketplace-reviews.repository/marketplace-reviews.types';

type ReportSummaryStreamHandlers = Pick<GetReportSummaryOptions, 'onProgress'>;

const parseEventLine = (line: string): ReportSummaryStreamEvent | null => {
    try {
        return JSON.parse(line) as ReportSummaryStreamEvent;
    } catch {
        return null;
    }
};

const handleEvent = (
    event: ReportSummaryStreamEvent | null,
    handlers: ReportSummaryStreamHandlers,
): AibackReportSummaryEntity | null => {
    if (!event) {
        return null;
    }

    if (event.type === 'started' || event.type === 'progress') {
        handlers.onProgress?.(event.progress);
        return null;
    }

    if (event.type === 'completed') {
        return event.summary;
    }

    if (event.type === 'error') {
        throw new Error(event.message || 'Failed to load AIBACK report summary');
    }

    return null;
};

export const consumeReportSummaryStream = async (
    response: Response,
    handlers: ReportSummaryStreamHandlers = {},
): Promise<AibackReportSummaryEntity> => {
    if (!response.body) {
        throw new Error('Missing AIBACK report summary stream body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffered = '';
    let completedSummary: AibackReportSummaryEntity | null = null;

    while (true) {
        const { done, value } = await reader.read();

        if (done) {
            break;
        }

        buffered += decoder.decode(value, { stream: true });

        while (true) {
            const lineBreakIndex = buffered.indexOf('\n');
            if (lineBreakIndex === -1) {
                break;
            }

            const line = buffered.slice(0, lineBreakIndex).trim();
            buffered = buffered.slice(lineBreakIndex + 1);

            if (!line) {
                continue;
            }

            const completedEventSummary = handleEvent(parseEventLine(line), handlers);
            if (completedEventSummary) {
                completedSummary = completedEventSummary;
            }
        }
    }

    const tail = buffered.trim();
    if (tail) {
        const completedEventSummary = handleEvent(parseEventLine(tail), handlers);
        if (completedEventSummary) {
            completedSummary = completedEventSummary;
        }
    }

    if (!completedSummary) {
        throw new Error('AIBACK report summary stream ended before completion');
    }

    return completedSummary;
};
